import { useEffect, useRef } from 'react'
import { Capacitor } from '@capacitor/core'
import { getNativeNotificationSettings, syncIosFcmTokenFromPreferences } from '@/lib/notifications'
import { useAuthState } from '@/hooks/useAuth'

/**
 * Hook to prompt users to enable notifications on iOS
 * - Triggers the iOS system prompt on first app load
 * - Does NOT automatically open settings if denied
 * - User can manually enable from Settings page if they want to try again
 */
export function useNotificationPrompt() {
  const { user } = useAuthState()
  const hasPromptedRef = useRef(false)

  useEffect(() => {
    // Prevent multiple prompts during the component lifecycle
    if (hasPromptedRef.current) return
    if (!user?.uid) return

    const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
    if (!isIOSNative) return

    const checkAndPromptNotifications = async () => {
      try {
        const isEnabled = await getNativeNotificationSettings()
        
        if (!isEnabled) {
          console.log('[useNotificationPrompt] Notifications disabled, attempting to prompt...')
          
          // Try to prompt for permissions
          await syncIosFcmTokenFromPreferences(user.uid)
          
          // If user denied, they can manually enable from Settings page
          // We don't open settings automatically here
          
          hasPromptedRef.current = true
        }
      } catch (error) {
        console.error('[useNotificationPrompt] Error checking/prompting notifications:', error)
      }
    }

    checkAndPromptNotifications()
  }, [user?.uid])
}
